import * as GuidedTourActions from 'app/guided-tour/state/guided-tour.actions';
import * as moment from 'moment';

import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Select, Store } from '@ngxs/store';
import {
  TrainingLoadParams,
  TrainingLoadType,
} from 'app/explore/models/training-load-params.model';
import {
  GetGroups,
  GetGroupTrainingLoadAnalyses,
  GetGroupTrainingLoads,
  GetIndividualTrainingLoadAnalyses,
  GetIndividualTrainingLoads,
  GetPlansPublishedToAllGroups,
  GetPlanTrainingLoadAnalyses,
  GetPlanTrainingLoads,
  GetTrainingLoadParams,
  SelectGroup,
  SelectMarine,
  SelectPlan,
  SetTabState,
} from 'app/explore/state/explore.actions';
import { Observable, Subscription } from 'rxjs';

import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { User } from 'app/classes/user';
import { GroupTrainingLoad } from 'app/explore/models/group-training-load.model';
import { IndividualTrainingLoad } from 'app/explore/models/individual-training-load.model';
import { PlanTrainingLoad } from 'app/explore/models/plan-training-load.model';
import { TabState } from 'app/explore/models/tab-state.model';
import { TrainingLoadAnalysis } from 'app/explore/models/training-load-analysis.model';
import { ExploreSelectors } from 'app/explore/state/explore.selectors';
import { Group } from 'app/group-management/models/group.model';
import { GuidedTourSelectors } from 'app/guided-tour/state/guided-tour.selectors';
import { AuthService } from 'app/services/auth.service';
import { GuidedTourDataService } from 'app/services/guided-tour-data.service';
import { OfflineService } from 'app/services/offline.service';
import { PlanMetadata } from 'fitforce-document-sync';

@Component({
  selector: 'app-explore',
  templateUrl: './explore.component.html',
  styleUrls: ['./explore.component.scss'],
})
export class ExploreComponent implements OnInit, AfterViewInit, OnDestroy {
  private subs: Subscription[] = [];

  readonly marineColumns = [
    'date',
    'workoutName',
    'groupName',
    'isComplete',
    'actualTrainingLoad',
    'estTrainingLoad',
  ];
  readonly planColumns = [
    'date',
    'phase',
    'playcardName',
    'actualTrainingLoad',
    'estTrainingLoad',
  ];
  readonly groupColumns = [
    'date',
    'planName',
    'playcardName',
    'actualTrainingLoad',
    'estTrainingLoad',
  ];
  readonly defaultNumWeeks = 6;

  loading = false;
  loadingTrainingLoads = false;
  loadingAnalyses = false;
  marines: User[] = [];
  plans: PlanMetadata[] = [];
  selectedGroupPlans: PlanMetadata[] = [];
  groups: Group[] = [];
  individualTrainingLoads: IndividualTrainingLoad[] = [];
  individualTrainingLoadAnalyses: TrainingLoadAnalysis[] = [];
  groupTrainingLoads: GroupTrainingLoad[] = [];
  planTrainingLoads: PlanTrainingLoad[] = [];
  selectedMarine: User;
  selectedMarineId: string;
  selectedPlan: PlanMetadata;
  selectedGroup: Group;
  offlineStatusSubscription: Subscription;
  tabState: TabState = 0;
  search: FormControl;
  pageNumber = 0;
  startDateForm = new FormControl(new Date(2020, 0, 1));
  numWeeksForm = new FormControl(52);
  weekRanges: TrainingLoadParams[] = [];
  selectedWeekRange = 0;
  firstWeekRangeSelected = false;
  lastWeekRangeSelected = false;

  lineSeries: number[] = [];
  barSeries: number[] = [];
  seriesLabels: string[] = [];
  lineLabel = 'Target Training Load';
  barLabel = 'Actual Training Load';

  dataSourceIndividuals = new MatTableDataSource<IndividualTrainingLoad>();
  dataSourcePlans = new MatTableDataSource<PlanTrainingLoad>();
  dataSourceGroups = new MatTableDataSource<GroupTrainingLoad>();

  @Select(ExploreSelectors.loading) loading$: Observable<boolean>;
  @Select(ExploreSelectors.loadingTrainingLoads)
  loadingTrainingLoads$: Observable<boolean>;
  @Select(ExploreSelectors.loadingAnalyses)
  loadingAnalyses$: Observable<boolean>;
  @Select(ExploreSelectors.marines) marines$: Observable<User[]>;
  @Select(ExploreSelectors.groups) groups$: Observable<Group[]>;
  @Select(ExploreSelectors.plans) plans$: Observable<PlanMetadata[]>;
  @Select(ExploreSelectors.selectedGroupPlans) selectedGroupPlans$: Observable<
    PlanMetadata[]
  >;
  @Select(ExploreSelectors.individualTrainingLoads)
  individualTrainingLoads$: Observable<IndividualTrainingLoad[]>;
  @Select(ExploreSelectors.individualTrainingLoadAnalyses)
  individualTrainingLoadAnalyses$: Observable<TrainingLoadAnalysis[]>;
  @Select(ExploreSelectors.planTrainingLoads) planTrainingLoads$: Observable<
    PlanTrainingLoad[]
  >;
  @Select(ExploreSelectors.planTrainingLoadAnalyses)
  planTrainingLoadAnalyses$: Observable<TrainingLoadAnalysis[]>;
  @Select(ExploreSelectors.groupTrainingLoads) groupTrainingLoads$: Observable<
    GroupTrainingLoad[]
  >;
  @Select(ExploreSelectors.groupTrainingLoadAnalyses)
  groupTrainingLoadAnalyses$: Observable<TrainingLoadAnalysis[]>;
  @Select(ExploreSelectors.selectedMarine) selectedMarine$: Observable<User>;
  @Select(ExploreSelectors.selectedPlan)
  selectedPlan$: Observable<PlanMetadata>;
  @Select(ExploreSelectors.selectedGroup) selectedGroup$: Observable<Group>;
  @Select(ExploreSelectors.tabState) tabState$: Observable<TabState>;
  @Select(ExploreSelectors.trainingLoadParams) trainingLoadParams$: Observable<
    TrainingLoadParams[]
  >;
  @ViewChild('paginator', { static: true }) paginator: MatPaginator;

  currentUser$: Observable<User>;
  currentUser: User;

  private guidedTourStatusSub: Subscription = new Subscription();

  guidedTourToggledOn = true;

  @Select(GuidedTourSelectors.toggleStatus)
  guidedTourToggleStatus$: Observable<boolean>;

  get isFormValid() {
    return this.selectedGroup;
  }

  constructor(
    private offline: OfflineService,
    private router: Router,
    private store: Store,
    private snackBar: MatSnackBar,
    private guidedTourDataService: GuidedTourDataService,
    private auth: AuthService,
  ) {}

  ngOnInit() {
    this.search = new FormControl(undefined);
    this.search.patchValue(undefined);
    this.currentUser$ = this.auth.currentUserObs;
    this.groups = [];
    this.marines = [];
    this.plans = [];
    this.lineSeries = [];
    this.barSeries = [];
    this.selectedMarine = undefined;
    this.selectedMarineId = undefined;
    this.selectedGroup = undefined;
    this.selectedPlan = undefined;

    this.subs.push(
      this.currentUser$.subscribe((user: User) => {
        this.currentUser = user;
      }),
      this.loading$.subscribe(value => {
        this.loading = value;
      }),
      this.loadingTrainingLoads$.subscribe(value => {
        this.loadingTrainingLoads = value;
        if (value) {
          this.clearChartData();
        }
      }),
      this.loadingAnalyses$.subscribe(value => {
        this.loadingAnalyses = value;
      }),
      this.marines$.subscribe(value => {
        this.marines = value;
      }),
      this.groups$.subscribe(value => {
        if (value) {
          this.groups = value;
          this.groups = this.groups
            .slice()
            .sort((a, b) =>
              a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1,
            );

          const groupUUIDs = this.groups.map(group => group.uuid);
          this.store.dispatch(new GetPlansPublishedToAllGroups(groupUUIDs));
        }
      }),
      this.plans$.subscribe(value => {
        const idSet = new Set<string>();
        this.plans = value
          .filter(plan => {
            if (
              !idSet.has(plan.uuid) &&
              this.currentUser &&
              plan.userUUID === this.currentUser.uuid
            ) {
              idSet.add(plan.uuid);
              return plan;
            }
          })
          .sort((a, b) =>
            a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1,
          );
      }),
      this.selectedGroupPlans$.subscribe(value => {
        this.selectedGroupPlans = value;
      }),
      this.individualTrainingLoads$.subscribe(value => {
        this.individualTrainingLoads = value;
        this.individualTrainingLoads = this.individualTrainingLoads
          .slice()
          .sort((a, b) => {
            return a.date < b.date ? -1 : a.date > b.date ? 1 : 0;
          })
          .map(tl => ({ ...tl, weekNumber: this.getWeekNumber(tl) }));

        this.dataSourceIndividuals.data = this.individualTrainingLoads;
        this.dataSourceIndividuals.paginator = this.paginator;
      }),
      this.individualTrainingLoadAnalyses$.subscribe(value => {
        if (this.weekRanges && this.weekRanges.length > 0) {
          this.buildChartData(value);
        }
      }),
      this.planTrainingLoads$.subscribe(value => {
        this.planTrainingLoads = value.map(tl => ({
          ...tl,
          weekNumber: this.getWeekNumber(tl),
        }));
        this.dataSourcePlans.data = value;
        this.dataSourcePlans.paginator = this.paginator;
      }),
      this.planTrainingLoadAnalyses$.subscribe(value => {
        this.buildChartData(value);
      }),
      this.groupTrainingLoads$.subscribe(value => {
        this.groupTrainingLoads = value.map(tl => ({
          ...tl,
          weekNumber: this.getWeekNumber(tl),
        }));
        this.dataSourceGroups.data = value;
        this.dataSourceGroups.paginator = this.paginator;
      }),
      this.groupTrainingLoadAnalyses$.subscribe(value => {
        this.buildChartData(value);
      }),
      this.selectedMarine$.subscribe(value => {
        this.selectedMarine = value;
      }),
      this.selectedPlan$.subscribe(value => {
        this.selectedPlan = value;
      }),
      this.selectedGroup$.subscribe(value => {
        this.selectedGroup = value;
      }),
      this.tabState$.subscribe(value => {
        this.tabState = value;
      }),
      this.trainingLoadParams$.subscribe(value => {
        if (value && value.length > 0) {
          this.weekRanges = value;
          const today = moment(moment.now());

          // select range containing current date; if current date not in range, select most recent
          this.selectedWeekRange = this.weekRanges.findIndex(w => {
            return today.isBetween(w.startDate, w.endDate);
          });
          if (this.selectedWeekRange === -1) {
            this.selectedWeekRange = this.weekRanges.length - 1;
          }

          // reload training load data using new params
          this.refreshData();
        }
      }),
    );
    this.offlineStatusSubscription = this.offline.status.subscribe(value => {
      const isOnline = value;
      if (!isOnline) {
        this.snackBar.open(
          'Explore is not available in offline mode.',
          'Dismiss',
          { duration: 5000 },
        );
        this.router.navigate(['dashboard']);
      }
    });

    this.store.dispatch(GetGroups);
  }

  ngAfterViewInit() {
    this.store.dispatch(new GuidedTourActions.GetGuidedTourToggleStatus());
    this.guidedTourStatusSub = this.guidedTourToggleStatus$.subscribe(
      status => {
        if (status !== undefined) {
          this.guidedTourToggledOn = status;
          if (this.guidedTourToggledOn) {
            setTimeout(
              () => this.guidedTourDataService.startGuidedTour('explore'),
              1000,
            );
          }
        }
      },
    );
  }

  ngOnDestroy() {
    this.offlineStatusSubscription.unsubscribe();
    if (this.guidedTourStatusSub) {
      this.guidedTourStatusSub.unsubscribe();
    }
    this.subs.forEach(sub => sub.unsubscribe());
  }

  selectMarine(marine: User) {
    this.store.dispatch(new SelectMarine(marine.uuid));
    this.store.dispatch(new SetTabState(TabState.MARINES));
    this.store.dispatch(
      new GetTrainingLoadParams(marine.uuid, TrainingLoadType.Individual),
    );
  }

  selectPlan(plan: PlanMetadata) {
    this.store.dispatch(new SelectPlan(plan));
    this.store.dispatch(new SetTabState(TabState.PLANS));
    this.store.dispatch(
      new GetTrainingLoadParams(this.selectedPlan.uuid, TrainingLoadType.Plan),
    );
  }

  selectGroup(group: Group) {
    this.store.dispatch(new SelectGroup(group));
    this.store.dispatch(new SetTabState(TabState.GROUPS));
    this.store.dispatch(
      new GetTrainingLoadParams(
        this.selectedGroup.uuid,
        TrainingLoadType.Group,
      ),
    );
  }

  nextWeekRange() {
    if (!this.loadingTrainingLoads && !this.loadingAnalyses) {
      this.selectedWeekRange++;
      if (this.selectedWeekRange >= this.weekRanges.length - 1) {
        this.selectedWeekRange = this.weekRanges.length - 1;
      }
      this.checkSelection();
      this.refreshData();
    }
  }

  prevWeekRange() {
    if (!this.loadingTrainingLoads && !this.loadingAnalyses) {
      this.selectedWeekRange--;
      if (this.selectedWeekRange <= 0) {
        this.selectedWeekRange = 0;
      }
      this.checkSelection();
      this.refreshData();
    }
  }

  checkSelection() {
    this.firstWeekRangeSelected = this.selectedWeekRange === 0;
    this.lastWeekRangeSelected =
      this.selectedWeekRange === this.weekRanges.length - 1;
  }

  refreshData() {
    const startDate: Date =
      this.weekRanges[this.selectedWeekRange].startDate.toDate();
    const endDate: Date =
      this.weekRanges[this.selectedWeekRange].endDate.toDate();

    switch (this.tabState) {
      case TabState.MARINES:
        this.store.dispatch(
          new GetIndividualTrainingLoads(
            this.selectedMarine,
            startDate,
            endDate,
          ),
        );
        this.store.dispatch(
          new GetIndividualTrainingLoadAnalyses(
            this.selectedMarine,
            startDate,
            endDate,
          ),
        );
        break;
      case TabState.PLANS:
        this.store.dispatch(
          new GetPlanTrainingLoads(this.selectedPlan, startDate, endDate),
        );
        this.store.dispatch(
          new GetPlanTrainingLoadAnalyses(
            this.selectedPlan,
            startDate,
            endDate,
          ),
        );
        break;
      case TabState.GROUPS:
        this.store.dispatch(
          new GetGroupTrainingLoads(this.selectedGroup, startDate, endDate),
        );
        this.store.dispatch(
          new GetGroupTrainingLoadAnalyses(
            this.selectedGroup,
            startDate,
            endDate,
          ),
        );
        break;
      default:
        break;
    }
  }

  buildChartData(value: any) {
    if (value && value.length > 0) {
      this.lineSeries = [];
      this.barSeries = [];
      this.seriesLabels = [];

      // sort results (remove this once data is returned sorted from backend)
      const analyses = value
        .slice()
        .sort((a, b) => a.weekNumber - b.weekNumber);
      const weekRange = this.weekRanges[this.selectedWeekRange];
      let week = weekRange.startWeek;
      let year = weekRange.startYear;

      const arraySize =
        weekRange.endWeek > weekRange.startWeek
          ? weekRange.endWeek - weekRange.startWeek
          : weekRange.endWeek < weekRange.startWeek
          ? weekRange.endWeek - weekRange.startWeek + 52
          : 1;
      const isDataForWeek: boolean[] = new Array(arraySize).fill(false);
      let i = 0;
      analyses.forEach(analysis => {
        isDataForWeek[analysis.weekNumber - 1] = true;
      });

      isDataForWeek.forEach(isData => {
        if (isData) {
          const analysis = analyses[i];
          this.lineSeries.push(analysis.meanEstTrainingLoad || 0);
          this.barSeries.push(analysis.meanActualTrainingLoad || 0);
          i++;
        } else {
          // push an empty data point if no training loads for that week
          this.lineSeries.push(0);
          this.barSeries.push(0);
        }
        const date = moment().week(week).weekYear(year).day(0);
        this.seriesLabels.push(date.format('M/D/YY'));
        year = week === 52 ? 1 + year : year;
        week = 1 + (week % 52);
      });
      this.checkSelection();
    } else {
      this.clearChartData();
    }
  }

  clearChartData() {
    this.dataSourceIndividuals.data = [];
    this.dataSourcePlans.data = [];
    this.dataSourceGroups.data = [];
    this.lineSeries = [];
    this.barSeries = [];
    this.seriesLabels = [];
  }

  getWeekNumber(tl: any): number {
    const date = moment(tl.date);
    return date.week();
  }
}
